<template>
  <section class="h-full mx-4" id="devicesManagement">
    <BaseTable
      v-if="devices"
      :headers="tableHeaders"
      :list="devices"
      table-id="devices"
      clickable
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item as Device)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useRouter } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import { removeDevicesByIds, getDevices } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type Device } from '@/types/Device';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const router = useRouter();
const devices = ref<Device[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'lastLogin', text: $t('device.lastLogin'), type: 'text', active: true },
  { value: 'remarks', text: $t('device.remarks'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  devices.value = await getDevices();
});

async function onRefreshData(data: TableFilterData) {
  let processedData = await getDevices();
  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'remarks']);
  }
  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  devices.value = processedData;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;

  const res = await removeDevicesByIds([idToDelete]);
  if (!res && devices.value)
    devices.value = devices.value.filter((device) => device.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteMultiselect(items: Device[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: 'Selected items cannot be deleted.' });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeDevicesByIds(idsToDelete);
  if (!res && devices.value)
    devices.value = devices.value.filter((device) => !idsToDelete.includes(device.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Device>();

function deleteSingleConfirm(item: Device) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onAddNew() {
  router.push({ name: 'device-details', params: { id: uuidv4() } });
}

function onItemClick(item: Device) {
  router.push({ name: 'device-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  onRefreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}
</script>
